// Sprint-16 — single notification row.
//
// Renders one inbox entry in two contexts:
//   • InboxDropdown — compact, 320px wide, max 8 visible.
//   • Inbox page    — same component, wider container.
//
// Anatomy:
//   ┌──────────────────────────────────────────────────────────┐
//   │ • [icon] Title text                              2h ago  │
//   │         Body excerpt — clamped to two lines.             │
//   └──────────────────────────────────────────────────────────┘
//
// Click behavior: mark read (optimistic), then navigate to row.url. If
// url is null (e.g. an ai_failure row pointing at a deleted guide), the
// click only marks read and stays put — silent fallback rather than a
// dead-link error.

import { useNavigate } from 'react-router-dom';
import { useTranslation} from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AtSign, Eye, Share2, AlertTriangle, Bell, KeyRound,
  Clock, CalendarClock,
} from 'lucide-react';
import { ThreadIcon } from '../icons/ThreadIcon';
import type { Notification, NotificationKind } from '../../types/notification';
import { useNotifications } from '../../stores/notificationsStore';
import { formatDateTime } from '../../lib/formatDate';

interface Props {
  notification: Notification;
  /** Optional click hook — used by InboxDropdown to close itself on click. */
  onClick?: () => void;
}

/**
 * Kind → Lucide icon. Exhaustive switch — adding a new kind in the union
 * forces TypeScript to error here until we add a case.
 *
 * Color rule: icons inherit color from the row (--ws-ink-2 default), with
 * danger kinds (ai_failure) using --ws-danger so the row visually
 * announces "something is wrong" at a glance.
 */
function iconFor(kind: NotificationKind) {
  switch (kind) {
    case 'thread_reply': return { Icon: ThreadIcon, accent: false };
    case 'mention':      return { Icon: AtSign,        accent: true  };
    case 'watch':        return { Icon: Eye,           accent: false };
    case 'invite':       return { Icon: Share2,        accent: false };
    case 'ai_failure':   return { Icon: AlertTriangle, accent: false, danger: true };
    // PR4 — access_request rows pop with the accent so an owner notices a
    // gate-open ask in their inbox stream.
    case 'access_request': return { Icon: KeyRound,    accent: true  };
    // BP 28 — training nudges. Due-soon is a neutral clock; overdue uses the
    // danger color so it reads as "act now" at a glance.
    case 'training_due_soon': return { Icon: CalendarClock, accent: false };
    case 'training_overdue':  return { Icon: Clock,         accent: false, danger: true };
    default:             return { Icon: Bell,          accent: false };
  }
}

export function NotificationItem({ notification, onClick }: Props) {
  const { t } = useTranslation('console');
  const markRead = useNotifications(s => s.markRead);
  const navigate = useNavigate();
  const isUnread = !notification.read_at;
  const { Icon, danger } = iconFor(notification.kind);

  async function handleClick() {
    onClick?.();
    if (isUnread) markRead(notification.id);
    if (notification.url) {
      // Use the router for in-app links (the server precomputes /guides/:id…
      // style paths). External URLs would need a window.location.assign;
      // we don't have those today, but if added, special-case here.
      navigate(notification.url);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-sm
                 transition-colors"
      style={{
        // Unread rows get a faint accent-quiet background so they stand out
        // even when read rows are present in the same list. Background sits
        // BEHIND the dot + icon column for a cohesive read.
        background: isUnread ? 'var(--ws-accent-quiet)' : 'transparent',
        color: 'var(--ws-ink)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = isUnread
          ? 'var(--ws-accent-quiet)'
          : 'var(--ws-surface-2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isUnread
          ? 'var(--ws-accent-quiet)'
          : 'transparent';
      }}
    >
      {/* Unread dot. Always rendered (as a spacer) so read + unread rows
          align — dot only visible when unread. 6px is the dot diameter +
          padding so the icon column lands at the same x in both states. */}
      <span
        aria-hidden="true"
        className="mt-1.5 shrink-0 rounded-full"
        style={{
          width:  6, height: 6,
          background: isUnread ? 'var(--ws-accent)' : 'transparent',
        }}
      />

      {/* Kind icon — color inherits unless danger. */}
      <span
        className="shrink-0 mt-0.5"
        style={{ color: danger ? 'var(--ws-danger)' : 'var(--ws-ink-3)' }}>
        <Icon size={15} strokeWidth={1.75} aria-hidden="true" />
      </span>

      {/* Title + body. Two lines max each via WebkitLineClamp so a chatty
          body excerpt doesn't blow out the dropdown height. */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-medium leading-tight"
             style={{ color: 'var(--ws-ink)' }}>
            {notification.title}
          </p>
          <span
            className="font-mono text-[11px] shrink-0 tabular-nums"
            style={{ color: 'var(--ws-ink-3)' }}
            title={formatDateTime(notification.created_at)}>
            {relativeTime(notification.created_at, t)}
          </span>
        </div>
        {notification.body && (
          <p
            className="text-[12px] mt-1 leading-snug"
            style={{
              color: 'var(--ws-ink-2)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {notification.body}
          </p>
        )}
      </div>
    </button>
  );
}

/**
 * Short relative-time string for the right-aligned timestamp. Mirrors the
 * one in MarkWatchedButton.tsx — duplicated rather than imported because
 * any change to one shouldn't silently change the other (the labels are
 * load-bearing for tests + visual consistency).
 */
function relativeTime(input: string, t: TFunction): string {
  const at = +new Date(input);
  if (!Number.isFinite(at)) return '';
  const diffMs = Date.now() - at;
  if (diffMs < 0) return t('notificationItem.relativeJustNow');
  const min = Math.floor(diffMs / 60_000);
  if (min < 1)  return t('notificationItem.relativeNow');
  if (min < 60) return t('notificationItem.relativeMinutes', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24)  return t('notificationItem.relativeHours', { count: hr });
  const day = Math.floor(hr / 24);
  if (day < 7)  return t('notificationItem.relativeDays', { count: day });
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
